import React, { useState } from "react";
import {
  useGetAllSystemOrdersForAdminQuery,
  useApproveOrderByAdminMutation,
} from "../../features/api/orderApiSlice";
import FuelLoading from "../../pages/FuelLoading";
import { useToast } from "../toast/ToastContext";
import ConfirmModal from "../modal/ConfirmModal";
import "./adminOrdersManager.scss";

const formatMoney = (value) => {
  if (value === null || value === undefined) return "—";

  const amount = Number(value);

  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "—";
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const getFulfillmentStage = (order) => {
  if (order.status === "canceled") return "canceled";
  if (order.status === "returned") return "returned";
  if (order.status === "failed") return "failed";
  if (order.status === "delivered") return "delivered";
  if (order.status === "shipped") return "shipped";

  if (order.paymentStatus !== "paid") {
    return "payment_incomplete";
  }

  if (order.submittedAt) return "submitted";
  if (order.approvedAt) return "approved";

  if (order.status === "succeeded") {
    return "awaiting_approval";
  }

  return "other";
};

const STAGE_LABELS = {
  awaiting_approval: "Awaiting Approval",
  approved: "Approved — Not Submitted",
  submitted: "Submitted for Fulfillment",
  shipped: "Shipped",
  delivered: "Delivered",
  canceled: "Canceled",
  returned: "Returned",
  failed: "Failed",
  payment_incomplete: "Payment Incomplete",
  other: "Review Required",
};

const FILTERS = [
  { value: "", label: "All Orders" },
  { value: "awaiting_approval", label: "⏳ Awaiting Approval" },
  { value: "approved", label: "✅ Approved" },
  { value: "submitted", label: "📦 Submitted" },
  { value: "shipped", label: "🚚 Shipped" },
  { value: "delivered", label: "🏠 Delivered" },
];

export default function AdminOrdersManager() {
  const [activeTab, setActiveTab] = useState("");
  const [orderToApprove, setOrderToApprove] = useState(null);

  const { showToast } = useToast();

  const {
    data: globalOrders = [],
    isLoading,
    isError,
  } = useGetAllSystemOrdersForAdminQuery("");

  const [approveOrder, { isLoading: isApproving }] =
    useApproveOrderByAdminMutation();

  const orders = Array.isArray(globalOrders) ? globalOrders : [];

  const filteredOrders =
    activeTab === ""
      ? orders
      : orders.filter((order) => getFulfillmentStage(order) === activeTab);

  const handleCloseApprovalModal = () => {
    if (isApproving) return;

    setOrderToApprove(null);
  };

  const handleApproveOrder = async () => {
    if (!orderToApprove || isApproving) return;

    try {
      await approveOrder(orderToApprove._id).unwrap();

      setOrderToApprove(null);

      showToast(
        "Order approved successfully. It has not been submitted to Printify.",
        "success"
      );
    } catch (error) {
      console.error("Order approval failed:", error);

      setOrderToApprove(null);

      showToast(
        error?.data?.message ||
          "Unable to approve this order. Please try again.",
        "error"
      );
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: "4rem" }}>
        <FuelLoading text="Loading master sales logs..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        style={{
          color: "#ef4444",
          padding: "4rem",
          textAlign: "center",
        }}
      >
        <h3>Failed to load order records.</h3>
      </div>
    );
  }

  return (
    <div className="admin-orders-manager-station fuel-fade-in">
      {/* REUSABLE CONFIRMATION MODAL */}
      <ConfirmModal
        isOpen={Boolean(orderToApprove)}
        variant="warning"
        title="Approve Merchandise Order"
        confirmText={isApproving ? "Approving..." : "Approve Order"}
        message={
          orderToApprove
            ? `Are you sure you want to approve order #${
                orderToApprove.orderNumber || "Unavailable"
              } for ${orderToApprove.customerName || "this customer"}?

Order Total: ${formatMoney(orderToApprove.financials?.total)}

This approves the order for fulfillment.

It will NOT submit the order to Printify or charge the customer again.`
            : ""
        }
        onConfirm={handleApproveOrder}
        onCancel={handleCloseApprovalModal}
      />

      {/* ORIGINAL PAGE HEADER */}
      <div className="dashboard-view-header">
        <h1>🧾 Master Sales Fulfillment Hub</h1>
        <p>
          Review customer orders, approve paid purchases, and monitor
          fulfillment progress.
        </p>
      </div>

      {/* ORIGINAL FILTER TAB LAYOUT */}
      <div className="status-filter-tabs-row">
        {FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            className={`tab-chip ${activeTab === filter.value ? "active" : ""}`}
            onClick={() => setActiveTab(filter.value)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* ORIGINAL ORDER CARDS CONTAINER */}
      <div className="orders-stack-list-container">
        {filteredOrders.length === 0 ? (
          <div className="empty-orders-view-fallback">
            No orders found for this selection.
          </div>
        ) : (
          filteredOrders.map((order) => {
            const stage = getFulfillmentStage(order);
            const canApprove = stage === "awaiting_approval";

            return (
              <div
                key={order._id}
                className={`order-manifest-card-row status-border--${order.status}`}
              >
                {/* ORIGINAL HEADER STRIP */}
                <div className="manifest-header-strip">
                  <div>
                    <h3>
                      Order:{" "}
                      <span>
                        {order.orderNumber
                          ? `#${order.orderNumber}`
                          : order.stripePaymentIntentId
                          ? `#${order.stripePaymentIntentId
                              .slice(-10)
                              .toUpperCase()}`
                          : "Unavailable"}
                      </span>
                    </h3>

                    <small className="timestamp-txt">
                      Placed on: {formatDate(order.createdAt)}
                    </small>
                  </div>

                  {/* FULFILLMENT STATE */}
                  <div className="operator-dropdown-action">
                    <span>Fulfillment State:</span>

                    <span className={`order-stage-label stage-${stage}`}>
                      {STAGE_LABELS[stage]}
                    </span>
                  </div>
                </div>

                {/* ORIGINAL THREE-COLUMN GRID */}
                <div className="manifest-details-flex-grid">
                  {/* COLUMN A — CUSTOMER & SHIPPING */}
                  <div className="details-col details-col--buyer">
                    <h4>👤 Consignee Destination</h4>

                    <strong style={{ color: "#fff" }}>
                      {order.customerName || "Customer"}
                    </strong>

                    <span className="email-link-text">
                      {order.customerEmail || "No email"}
                    </span>

                    <span className="checkout-type-label">
                      {order.checkoutType === "registered"
                        ? "Registered Checkout"
                        : "Guest Checkout"}
                    </span>

                    <div className="address-box-display">
                      <div>{order.shippingAddress?.street || "—"}</div>

                      {order.shippingAddress?.addressLine2 && (
                        <div>{order.shippingAddress.addressLine2}</div>
                      )}

                      <div>
                        {[
                          order.shippingAddress?.city,
                          order.shippingAddress?.state,
                          order.shippingAddress?.zipCode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </div>

                      {order.shippingAddress?.country && (
                        <div>{order.shippingAddress.country}</div>
                      )}
                    </div>
                  </div>

                  {/* COLUMN B — PRODUCTS */}
                  <div className="details-col details-col--items">
                    <h4>📦 Packed Line Items Allocation</h4>

                    <div className="items-pack-rows-tray">
                      {order.items?.map((item, index) => (
                        <div
                          key={item._id || index}
                          className="item-row-pack-node"
                        >
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name || "Product"}
                              className="node-thumb"
                            />
                          ) : (
                            <div className="node-thumb node-thumb-empty">
                              📦
                            </div>
                          )}

                          <div className="node-meta">
                            <strong>{item.name}</strong>

                            <span className="specs-subtext">
                              Qty: {item.quantity}
                              {item.size && ` | Size: ${item.size}`}
                              {(item.colorName || item.color) &&
                                ` | Color: ${item.colorName || item.color}`}
                            </span>

                            <span className="fulfillment-provider-label">
                              {item.fulfillmentMethod === "printify"
                                ? "Printify"
                                : item.fulfillmentMethod === "self"
                                ? "Self Fulfilled"
                                : item.fulfillmentMethod === "local_partner"
                                ? "Local Partner"
                                : "Fulfillment Review"}
                            </span>
                          </div>

                          <span className="node-line-price">
                            {formatMoney(
                              item.lineSubtotalCents != null
                                ? item.lineSubtotalCents / 100
                                : Number(item.price || 0) *
                                    Number(item.quantity || 0)
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* COLUMN C — FINANCIALS */}
                  <div className="details-col details-col--financials">
                    <h4>💸 Financial Ledger Receipt</h4>

                    <div className="ledger-breakdown-table">
                      <div>
                        <span>Subtotal:</span>
                        <span>{formatMoney(order.financials?.subtotal)}</span>
                      </div>

                      {Number(order.financials?.discount) > 0 && (
                        <div className="discount-row">
                          <span>Discount Code:</span>
                          <span>-{formatMoney(order.financials.discount)}</span>
                        </div>
                      )}

                      <div>
                        <span>Shipping:</span>
                        <span>{formatMoney(order.financials?.shipping)}</span>
                      </div>

                      <div>
                        <span>Sales Tax:</span>
                        <span>{formatMoney(order.financials?.tax)}</span>
                      </div>

                      <div className="grand-total-row">
                        <span>Grand Total:</span>
                        <span className="total-gold-txt">
                          {formatMoney(order.financials?.total)}
                        </span>
                      </div>
                    </div>

                    {order.couponApplied && order.couponApplied !== "none" && (
                      <div className="coupon-badge-line-tag">
                        🎟️ Coupon Applied:{" "}
                        <strong>{order.couponApplied}</strong>
                      </div>
                    )}
                  </div>
                </div>

                {/* APPROVAL ACTION BELOW ORIGINAL GRID */}
                {canApprove && (
                  <div className="order-approval-action-row">
                    <span>Payment received. Awaiting manual approval.</span>

                    <button
                      type="button"
                      className="approve-order-btn"
                      disabled={isApproving}
                      onClick={() => setOrderToApprove(order)}
                    >
                      Approve Order
                    </button>
                  </div>
                )}

                {stage === "approved" && (
                  <div className="order-approval-action-row">
                    <span>
                      Approved on {formatDate(order.approvedAt)}. Awaiting
                      Printify submission.
                    </span>
                  </div>
                )}

                {stage === "submitted" && (
                  <div className="order-approval-action-row">
                    <span>
                      Submitted for fulfillment on{" "}
                      {formatDate(order.submittedAt)}.
                    </span>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// import React, { useState } from "react";
// import {
//   useGetAllSystemOrdersForAdminQuery,
//   useApproveOrderByAdminMutation,
// } from "../../features/api/orderApiSlice";
// import FuelLoading from "../../pages/FuelLoading";
// import "./adminOrdersManager.scss";

// export default function AdminOrdersManager() {
//   const [activeTab, setActiveTab] = useState("");
//   const [orderToApprove, setOrderToApprove] = useState(null);

//   const {
//     data: globalOrders = [],
//     isLoading,
//     isError,
//   } = useGetAllSystemOrdersForAdminQuery("");

//   const [approveOrder, { isLoading: isApproving }] =
//     useApproveOrderByAdminMutation();

//   const getOrderStage = (order) => {
//     if (order.status === "canceled") return "canceled";
//     if (order.status === "returned") return "returned";
//     if (order.status === "failed") return "failed";
//     if (order.status === "delivered") return "delivered";
//     if (order.status === "shipped") return "shipped";

//     if (order.paymentStatus !== "paid") return "unpaid";

//     if (order.submittedAt) return "submitted";
//     if (order.approvedAt) return "approved";

//     if (order.status === "succeeded") {
//       return "awaiting_approval";
//     }

//     return "other";
//   };

//   const orders = Array.isArray(globalOrders) ? globalOrders : [];

//   const filteredOrders = orders.filter((order) => {
//     if (activeTab === "") return true;
//     return getOrderStage(order) === activeTab;
//   });

//   const handleApproveOrder = async () => {
//     if (!orderToApprove || isApproving) return;

//     try {
//       await approveOrder(orderToApprove._id).unwrap();

//       setOrderToApprove(null);

//       alert(
//         "Order approved successfully. It has not been submitted to Printify."
//       );
//     } catch (err) {
//       console.error("Order approval failed:", err);

//       alert(err?.data?.message || "Unable to approve this order.");

//       setOrderToApprove(null);
//     }
//   };

//   if (isLoading)
//     return (
//       <div style={{ padding: "4rem" }}>
//         <FuelLoading text="Loading master sales logs..." />
//       </div>
//     );

//   if (isError)
//     return (
//       <div
//         style={{
//           color: "#ef4444",
//           padding: "4rem",
//           textAlign: "center",
//         }}
//       >
//         <h3>Failed to load order records.</h3>
//       </div>
//     );

//   return (
//     <div className="admin-orders-manager-station fuel-fade-in">
//       {/* ORIGINAL PAGE HEADER */}
//       <div className="dashboard-view-header">
//         <h1>🧾 Master Sales Fulfillment Hub</h1>
//         <p>
//           Review customer orders, approve paid purchases, and monitor
//           fulfillment progress.
//         </p>
//       </div>

//       {/* ORIGINAL FILTER TAB LAYOUT */}
//       <div className="status-filter-tabs-row">
//         {[
//           ["", "All Orders"],
//           ["awaiting_approval", "⏳ Awaiting Approval"],
//           ["approved", "✅ Approved"],
//           ["submitted", "📦 Submitted"],
//           ["shipped", "🚚 Shipped"],
//           ["delivered", "🏠 Delivered"],
//         ].map(([value, label]) => (
//           <button
//             key={value}
//             type="button"
//             className={`tab-chip ${activeTab === value ? "active" : ""}`}
//             onClick={() => setActiveTab(value)}
//           >
//             {label}
//           </button>
//         ))}
//       </div>

//       {/* ORIGINAL ORDER CARDS CONTAINER */}
//       <div className="orders-stack-list-container">
//         {filteredOrders.length === 0 ? (
//           <div className="empty-orders-view-fallback">
//             No orders found for this selection.
//           </div>
//         ) : (
//           filteredOrders.map((order) => {
//             const stage = getOrderStage(order);

//             const canApprove = stage === "awaiting_approval";

//             return (
//               <div
//                 key={order._id}
//                 className={`order-manifest-card-row status-border--${order.status}`}
//               >
//                 {/* ORIGINAL HEADER STRIP */}
//                 <div className="manifest-header-strip">
//                   <div>
//                     <h3>
//                       Order:{" "}
//                       <span>
//                         {order.orderNumber
//                           ? `#${order.orderNumber}`
//                           : `#${order.stripePaymentIntentId
//                               ?.slice(-10)
//                               .toUpperCase()}`}
//                       </span>
//                     </h3>

//                     <small className="timestamp-txt">
//                       Placed on: {new Date(order.createdAt).toLocaleString()}
//                     </small>
//                   </div>

//                   {/* REPLACES OLD STATUS DROPDOWN */}
//                   <div className="operator-dropdown-action">
//                     <span>Fulfillment State:</span>

//                     <span className={`order-stage-label stage-${stage}`}>
//                       {stage === "awaiting_approval"
//                         ? "Awaiting Approval"
//                         : stage === "approved"
//                         ? "Approved — Not Submitted"
//                         : stage === "submitted"
//                         ? "Submitted"
//                         : stage === "shipped"
//                         ? "Shipped"
//                         : stage === "delivered"
//                         ? "Delivered"
//                         : stage === "canceled"
//                         ? "Canceled"
//                         : stage === "returned"
//                         ? "Returned"
//                         : stage === "failed"
//                         ? "Failed"
//                         : stage === "unpaid"
//                         ? "Payment Incomplete"
//                         : "Review Required"}
//                     </span>
//                   </div>
//                 </div>

//                 {/* ORIGINAL THREE-COLUMN GRID */}
//                 <div className="manifest-details-flex-grid">
//                   {/* COLUMN A — CUSTOMER */}
//                   <div className="details-col details-col--buyer">
//                     <h4>👤 Consignee Destination</h4>

//                     <strong style={{ color: "#fff" }}>
//                       {order.customerName}
//                     </strong>

//                     <span className="email-link-text">
//                       {order.customerEmail}
//                     </span>

//                     <div className="address-box-display">
//                       {order.shippingAddress?.street}
//                       <br />
//                       {order.shippingAddress?.city},{" "}
//                       {order.shippingAddress?.zipCode}
//                     </div>
//                   </div>

//                   {/* COLUMN B — PRODUCTS */}
//                   <div className="details-col details-col--items">
//                     <h4>📦 Packed Line Items Allocation</h4>

//                     <div className="items-pack-rows-tray">
//                       {order.items?.map((item, idx) => (
//                         <div key={idx} className="item-row-pack-node">
//                           <img src={item.image} alt="" className="node-thumb" />

//                           <div className="node-meta">
//                             <strong>{item.name}</strong>

//                             <span className="specs-subtext">
//                               Qty: {item.quantity}
//                               {item.size && ` | Size: ${item.size}`}
//                               {item.colorName && ` | Color: ${item.colorName}`}
//                             </span>
//                           </div>

//                           <span className="node-line-price">
//                             ${(item.price * item.quantity).toFixed(2)}
//                           </span>
//                         </div>
//                       ))}
//                     </div>
//                   </div>

//                   {/* COLUMN C — FINANCIALS */}
//                   <div className="details-col details-col--financials">
//                     <h4>💸 Financial Ledger Receipt</h4>

//                     <div className="ledger-breakdown-table">
//                       <div>
//                         <span>Subtotal:</span>
//                         <span>${order.financials?.subtotal?.toFixed(2)}</span>
//                       </div>

//                       {order.financials?.discount > 0 && (
//                         <div className="discount-row">
//                           <span>Discount Code:</span>
//                           <span>
//                             -$
//                             {order.financials.discount.toFixed(2)}
//                           </span>
//                         </div>
//                       )}

//                       <div>
//                         <span>Shipping:</span>
//                         <span>${order.financials?.shipping?.toFixed(2)}</span>
//                       </div>

//                       <div>
//                         <span>Sales Tax:</span>
//                         <span>${order.financials?.tax?.toFixed(2)}</span>
//                       </div>

//                       <div className="grand-total-row">
//                         <span>Grand Total:</span>
//                         <span className="total-gold-txt">
//                           ${order.financials?.total?.toFixed(2)}
//                         </span>
//                       </div>
//                     </div>

//                     {order.couponApplied && order.couponApplied !== "none" && (
//                       <div className="coupon-badge-line-tag">
//                         🎟️ Coupon Applied:{" "}
//                         <strong>{order.couponApplied}</strong>
//                       </div>
//                     )}
//                   </div>
//                 </div>

//                 {/* NEW APPROVAL ACTION — BELOW ORIGINAL GRID */}
//                 {canApprove && (
//                   <div className="order-approval-action-row">
//                     <span>Payment received. Awaiting manual approval.</span>

//                     <button
//                       type="button"
//                       className="approve-order-btn"
//                       disabled={isApproving}
//                       onClick={() => setOrderToApprove(order)}
//                     >
//                       Approve Order
//                     </button>
//                   </div>
//                 )}

//                 {stage === "approved" && (
//                   <div className="order-approval-action-row">
//                     <span>
//                       Approved on {new Date(order.approvedAt).toLocaleString()}.
//                       Awaiting Printify submission.
//                     </span>
//                   </div>
//                 )}
//               </div>
//             );
//           })
//         )}
//       </div>

//       {/* CONFIRMATION DIALOG */}
//       {orderToApprove && (
//         <div className="approval-modal-overlay">
//           <div
//             className="approval-modal"
//             role="dialog"
//             aria-modal="true"
//             aria-labelledby="approval-modal-title"
//           >
//             <h2 id="approval-modal-title">Approve This Order?</h2>

//             <p>
//               Order <strong>#{orderToApprove.orderNumber}</strong>
//             </p>

//             <p>
//               Customer: <strong>{orderToApprove.customerName}</strong>
//             </p>

//             <p>
//               Total:{" "}
//               <strong>${orderToApprove.financials?.total?.toFixed(2)}</strong>
//             </p>

//             <p className="approval-modal-warning">
//               This approves the order for fulfillment. It will not submit the
//               order to Printify or charge the customer again.
//             </p>

//             <div className="approval-modal-actions">
//               <button
//                 type="button"
//                 className="cancel-approval-btn"
//                 disabled={isApproving}
//                 onClick={() => setOrderToApprove(null)}
//               >
//                 Go Back
//               </button>

//               <button
//                 type="button"
//                 className="confirm-approval-btn"
//                 disabled={isApproving}
//                 onClick={handleApproveOrder}
//               >
//                 {isApproving ? "Approving..." : "Confirm Approval"}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }
